import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { io } from 'socket.io-client'; // Import Socket.IO client
import { isAuthenticated, logout, getUserRole } from '../../utils/auth'; // Assuming these are in '../auth'
import Logo from '../../components/images/logo.png'
import { BACKEND_URL } from '../../Constants';


const CoursesPublic = () => {
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [socket, setSocket] = useState(null); // Add socket state
    const [studentId, setStudentId] = useState(null); // State to store studentId
    const [userRole, setUserRole] = useState(null); // State to store user role

    const navigate = useNavigate();

    const handleLogout = () => {
        logout(() => {
            navigate('/'); // Redirect to home after logout
        });
    };

    const fetchPublicCourses = async () => {
        setLoading(true);
        setError(null);
        try {
            const publicCoursesResponse = await axios.get(`${BACKEND_URL}/api/courses/public`);
            let publicCourses = publicCoursesResponse.data;

            // If a student is logged in, fetch their enrolled courses
            if (studentId && userRole === 'student') {
                const enrolledCoursesResponse = await axios.get(`${BACKEND_URL}/api/enrollments/student/${studentId}/courses`);
                const enrolledCourses = enrolledCoursesResponse.data;

                // Create a map for quick lookup of enrolled courses by courseId
                const enrolledMap = new Map();
                enrolledCourses.forEach(ec => {
                    enrolledMap.set(ec._id, ec.enrollmentStatus);
                });

                // Augment public courses with enrollment status
                publicCourses = publicCourses.map(course => ({
                    ...course,
                    enrollmentStatus: enrolledMap.get(course._id) || null // null if not enrolled
                }));
            }
            setCourses(publicCourses);
        } catch (err) {
            console.error("Error fetching public courses or enrollment status:", err);
            setError(err.message || 'Failed to fetch courses');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Set studentId and userRole from localStorage
        const id = localStorage.getItem('userId');
        const role = getUserRole(); // Assuming getUserRole() extracts role from token
        setStudentId(id);
        setUserRole(role);

        // Initialize Socket.IO connection
        const newSocket = io(`${BACKEND_URL}`);
        setSocket(newSocket);

        // Fetch initial data
        fetchPublicCourses(); // Call after setting studentId and userRole

        // Set up event listener for 'course-updated'
        newSocket.on('course-updated', (updatedCourse) => {
            console.log('Received course-updated event:', updatedCourse); // Debugging
             fetchPublicCourses(); // Re-fetch all courses to get latest status
        });

        // Clean up socket connection on unmount
        return () => {
            newSocket.disconnect();
        };
    }, [studentId, userRole]); // Add studentId and userRole to dependencies to re-fetch when they change

    if (loading) {
        return (
            <div className="container-fluid">
                <div className="row">
                    {[...Array(3)].map((_, i) => (
                        <div key={i} className="col-12 col-md-6 col-lg-4 mb-4">
                            <div className="card">
                                <div className="card-body">
                                    <div className="skeleton skeleton-heading"></div>
                                    <div className="skeleton skeleton-body"></div>
                                    <div className="skeleton skeleton-text"></div>
                                    <div className="skeleton skeleton-button"></div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mt-4 alert alert-danger" role="alert">
                Error: {error}
            </div>
        );
    }

    if (courses.length === 0) {
        return (
            <div className="container mt-4 alert alert-info" role="alert">
                No public courses available at this time.
            </div>
        );
    }

    return (
        <>
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            {/* Navigation Bar */}
            <nav style={{ backgroundColor: '#f8f9fa', padding: '15px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee' }}>
                {/* Logo (replace with your actual logo) */}
                <div style={{ fontSize: '1.5em', fontWeight: 'bold', color: '#007bff' }}>
                <Link to="/"><img src={Logo} alt="Learning Aids" height={ 35 } /></Link>
                </div>
                {/* Navigation Links */}
                <div className='header-link-block'>
                <div className='navlist'>
                    <ul>
                    <li><Link to="/courses">Courses</Link></li>
                    </ul>
                </div>
                {isAuthenticated() ? (
                    <>
                    <button
                        style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '8px 15px', borderRadius: '5px', cursor: 'pointer', textDecoration: 'none' }}
                        onClick={handleLogout}
                    >
                        Logout
                    </button>
                    <button
                        style={{ backgroundColor: '#007bff', color: 'white', border: 'none', padding: '8px 15px', borderRadius: '5px', cursor: 'pointer', textDecoration: 'none' }}
                        onClick={() => navigate('/dashboard')}
                    >
                        Dashboard
                    </button>
                    </>
                ) : (
                    <>
                    <Link to="/login" style={{ color: '#007bff', textDecoration: 'none', marginRight: '15px' }}>Login</Link>
                    <Link to="/register" style={{ color: '#007bff', textDecoration: 'none' }}>Register</Link>
                    </>
                )}
                </div>
            </nav>
            <div className="container-fluid pt-4">
                <div className="row">
                    {courses.map((course) => (
                        <div key={course._id} className="col-12 col-md-6 col-lg-4 mb-4">
                            <div className="card h-100">
                                <div className="card-body">
                                    <h2 className="card-title">{course.title}</h2>
                                    <p className="card-text text-muted">
                                    </p>
                                    <span className="badge bg-secondary mb-3">
                                        License: {course.license}
                                    </span>
                                    {isAuthenticated() && userRole === 'student' ? (
                                        course.enrollmentStatus ? (
                                            <>
                                                <span className={`badge mb-3 me-2 ${
                                                    course.enrollmentStatus === 'approved' || course.enrollmentStatus === 'completed'
                                                        ? 'bg-success'
                                                        : course.enrollmentStatus === 'pending'
                                                        ? 'bg-warning'
                                                        : 'bg-danger'
                                                }`}>
                                                    Status: {course.enrollmentStatus.charAt(0).toUpperCase() + course.enrollmentStatus.slice(1)}
                                                </span>
                                                <Link to={`/course/${course._id}`} className="btn btn-primary w-100">
                                                    View Course
                                                </Link>
                                            </>
                                        ) : (
                                            <Link to={`/course/${course._id}`} className="btn btn-primary w-100">
                                                Enroll / View Course
                                            </Link>
                                        )
                                    ) : (
                                        <Link to={`/course/${course._id}`} className="btn btn-primary w-100">
                                            View Course
                                        </Link>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
        </>
    );
};

export default CoursesPublic;
